'use client'

import React, { useEffect, useState } from 'react'

import { Box, Container, Typography, Card, Chip, Button, Tabs, Tab } from '@mui/material'
import { styled } from '@mui/material/styles'
import AOS from 'aos'

import primaryColorConfig from '../configs/primaryColorConfig'
import 'aos/dist/aos.css'

const StyledServiceCard = styled(Card)({
  borderRadius: 32,
  overflow: 'hidden',
  height: '100%',
  background: 'rgba(255,255,255,0.05)',
  backdropFilter: 'blur(30px)',
  border: '1px solid rgba(255,255,255,0.1)',
  transition: 'all 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
  position: 'relative',
  cursor: 'pointer',
  '&:hover': {
    transform: 'translateY(-15px) scale(1.02)',
    boxShadow: '0 30px 60px rgba(0,0,0,0.3)',
    background: 'rgba(255,255,255,0.1)',
    '& .service-glow': {
      opacity: 1,
      transform: 'scale(1.1)'
    },
    '& .feature-item': {
      transform: 'translateX(6px)',
      background: 'rgba(255,255,255,0.15)'
    },
    '& .service-icon': {
      transform: 'rotate(360deg) scale(1.15)'
    }
  }
})

const FeatureGrid = styled(Box)({
  display: 'grid',
  gridTemplateColumns: '1fr',
  gap: '10px',
  '@media (min-width: 900px)': {
    gridTemplateColumns: 'repeat(2, 1fr)'
  }
})

const ServiceIcon = styled(Box)({
  width: 100,
  height: 100,
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '2.5rem',
  margin: '0 auto 20px',
  transition: 'all 0.6s ease',
  position: 'relative',
  zIndex: 2
})

const Service = () => {
  const [activeTab, setActiveTab] = useState(0)

  const serviceCategories = [
    { label: 'Dewasa', icon: '👨‍👩‍👧‍👦' },
    { label: 'Anak', icon: '👶' }
  ]

  const services = [
    // Dewasa Services
    [
      {
        title: 'Basic Package',
        subtitle: 'Paket Ekonomis Dewasa',
        description:
          'Paket layanan pemakaman standar untuk dewasa dengan fasilitas utama yang mendukung kebutuhan dasar prosesi persemayaman selama 2 hari.',
        icon: '🏠',
        color: primaryColorConfig[0].main,
        gradient: `linear-gradient(135deg, ${primaryColorConfig[0].main} 0%, ${primaryColorConfig[1].main} 100%)`,
        glowColor: primaryColorConfig[0].light,
        duration: '2 Hari',
        features: [
          { name: '1 Ruangan Semayam (2 Tanggal)', icon: '🏠', highlight: true },
          { name: 'Perawatan & Perlengkapan Jenazah', icon: '🧼' },
          { name: 'Formalin Standar', icon: '💉' },
          { name: 'Paket Dekorasi Kasih', icon: '🌸' },
          { name: 'Mobil Jenazah (Jemput + Antar)', icon: '🚗' },
          { name: 'Area Tasikmalaya', icon: '📍' },
          { name: 'Peti Basic (Verona Premium)', icon: '📦' },
          { name: 'Ruang Keluarga', icon: '🪑' }
        ],
        price: 'Mulai 3.5 Juta',
        popular: false
      },
      {
        title: 'Premium Package',
        subtitle: 'Paket Lengkap Dewasa',
        description:
          'Paket layanan pemakaman premium untuk dewasa dengan fasilitas eksklusif dan pelayanan terbaik selama 3 hari dengan tambahan dokumentasi dan konsumsi.',
        icon: '👑',
        color: primaryColorConfig[1].main,
        gradient: `linear-gradient(135deg, ${primaryColorConfig[1].main} 0%, ${primaryColorConfig[2].main} 100%)`,
        glowColor: primaryColorConfig[1].light,
        duration: '3 Hari',
        features: [
          { name: '1 Ruangan Semayam (3 Tanggal)', icon: '🏠', highlight: true },
          { name: 'Perawatan & Perlengkapan Jenazah', icon: '🧼' },
          { name: 'Formalin Standar', icon: '💉' },
          { name: 'Paket Dekorasi Damai', icon: '🌺' },
          { name: 'Mobil Jenazah (Jemput + Antar)', icon: '🚗' },
          { name: 'Area Tasikmalaya', icon: '📍' },
          { name: 'Paket Dokumentasi Lengkap', icon: '📷' },
          { name: '20 Nasi Kotak', icon: '🍱' },
          { name: 'Air Minum Kemasan (2 Dus)', icon: '🥤' },
          { name: 'Ruang Keluarga Premium', icon: '🛋️' }
        ],
        price: 'Mulai 6.5 Juta',
        popular: true
      }
    ],

    // Anak Services
    [
      {
        title: 'Basic Package Anak',
        subtitle: 'Paket Khusus Anak-Anak',
        description:
          'Paket layanan pemakaman khusus untuk anak-anak dengan pendekatan yang lembut dan fasilitas yang disesuaikan selama 2 hari.',
        icon: '👶',
        color: primaryColorConfig[2].main,
        gradient: `linear-gradient(135deg, ${primaryColorConfig[2].main} 0%, ${primaryColorConfig[3].main} 100%)`,
        glowColor: primaryColorConfig[2].light,
        duration: '2 Hari',
        features: [
          { name: '1 Ruangan Semayam (2 Tanggal)', icon: '🏠', highlight: true },
          { name: 'Perawatan Khusus Anak', icon: '🧼' },
          { name: 'Formalin Standar', icon: '💉' },
          { name: 'Paket Dekorasi Kasih', icon: '🌸' },
          { name: 'Mobil Jenazah (Jemput + Antar)', icon: '🚗' },
          { name: 'Area Tasikmalaya', icon: '📍' },
          { name: 'Peti Basic (Verona Premium)', icon: '📦' },
          { name: 'Ruang Keluarga Nyaman', icon: '🪑' }
        ],
        price: 'Mulai 2.8 Juta',
        popular: false
      },
      {
        title: 'Premium Package Anak',
        subtitle: 'Paket Lengkap Anak-Anak',
        description:
          'Paket layanan pemakaman premium untuk anak-anak dengan fasilitas terlengkap dan perhatian khusus selama 3 hari dengan dokumentasi dan konsumsi.',
        icon: '🌟',
        color: primaryColorConfig[3].main,
        gradient: `linear-gradient(135deg, ${primaryColorConfig[3].main} 0%, ${primaryColorConfig[4].main} 100%)`,
        glowColor: primaryColorConfig[3].light,
        duration: '3 Hari',
        features: [
          { name: '1 Ruangan Semayam (3 Tanggal)', icon: '🏠', highlight: true },
          { name: 'Perawatan Premium Anak', icon: '🧼' },
          { name: 'Formalin Standar', icon: '💉' },
          { name: 'Paket Dekorasi Damai', icon: '🌺' },
          { name: 'Mobil Jenazah (Jemput + Antar)', icon: '🚗' },
          { name: 'Area Tasikmalaya', icon: '📍' },
          { name: 'Paket Dokumentasi Lengkap', icon: '📷' },
          { name: '20 Nasi Kotak', icon: '🍱' },
          { name: 'Air Minum Kemasan (2 Dus)', icon: '🥤' },
          { name: 'Ruang Keluarga Premium', icon: '🛋️' }
        ],
        price: 'Mulai 5.2 Juta',
        popular: true
      }
    ]
  ]

  useEffect(() => {
    AOS.init({
      duration: 800,
      once: true,
      easing: 'ease-out-cubic'
    })
  }, [])

  const currentServices = services[activeTab]

  return (
    <Box
      component='section'
      sx={{
        py: { xs: 8, md: 12 },
        background: `linear-gradient(135deg, ${primaryColorConfig[0].dark} 0%, ${primaryColorConfig[0].main} 50%, ${primaryColorConfig[1].main} 100%)`,
        position: 'relative',
        minHeight: '100vh',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background:
            'radial-gradient(circle at 30% 70%, rgba(102, 126, 234, 0.1) 0%, transparent 50%), radial-gradient(circle at 70% 30%, rgba(240, 147, 251, 0.1) 0%, transparent 50%)',
          animation: 'float 8s ease-in-out infinite'
        },
        '@keyframes float': {
          '0%, 100%': { transform: 'translateY(0px) rotate(0deg)' },
          '50%': { transform: 'translateY(-20px) rotate(180deg)' }
        }
      }}
      id='services'
    >
      <Container maxWidth='xl' sx={{ position: 'relative', zIndex: 1 }}>
        {/* Header */}
        <Box textAlign='center' mb={6} data-aos='fade-down'>
          <Typography
            id='facilities-title'
            data-aos={'fade-down'}
            variant='h2'
            component='h2'
            gutterBottom
            sx={{
              textAlign: 'center',
              fontWeight: 700,
              mb: { xs: 4, md: 6 },
              fontSize: { xs: '2rem', md: '2.5rem' },
              color: 'white',
              position: 'relative',
              '&::after': {
                content: '""',
                position: 'absolute',
                bottom: -12,
                left: '50%',
                transform: 'translateX(-50%)',
                width: '60px',
                height: '4px',
                backgroundColor: 'white',
                borderRadius: '2px'
              }
            }}
          >
            Paket Layanan Kami
          </Typography>

          <Typography
            data-aos={'fade-down'}
            variant='h6'
            component='p'
            sx={{
              textAlign: 'center',
              mb: { xs: 4, md: 6 },
              maxWidth: '700px',
              mx: 'auto',
              color: 'white',
              lineHeight: 1.6
            }}
          >
            Pilih paket yang sesuai dengan kebutuhan dan memberikan penghormatan terbaik
          </Typography>
        </Box>

        {/* Category Tabs */}
        <Box display='flex' justifyContent='center' mb={6} data-aos='fade-up'>
          <Tabs
            value={activeTab}
            onChange={(e, newValue) => setActiveTab(newValue)}
            sx={{
              '& .MuiTabs-indicator': {
                backgroundColor: 'transparent'
              }
            }}
          >
            {serviceCategories.map((category, index) => (
              <Tab
                key={index}
                label={
                  <Box display='flex' alignItems='center' gap={1}>
                    <span style={{ fontSize: '1.5rem' }}>{category.icon}</span>
                    <Typography variant='h6' fontWeight={600} color='white'>
                      {category.label}
                    </Typography>
                  </Box>
                }
                sx={{
                  mx: 2,
                  px: 4,
                  py: 2,
                  borderRadius: '25px',
                  background:
                    activeTab === index
                      ? `linear-gradient(135deg, ${primaryColorConfig[0].main} 0%, ${primaryColorConfig[1].main} 100%)`
                      : 'rgba(255,255,255,0.1)',
                  color: 'white',
                  fontSize: '1.1rem',
                  border: '1px solid rgba(255,255,255,0.2)',
                  backdropFilter: 'blur(10px)',
                  transition: 'all 0.3s ease',
                  minHeight: '60px',
                  '&:hover': {
                    background: `linear-gradient(135deg, ${primaryColorConfig[0].main} 0%, ${primaryColorConfig[1].main} 100%)`,
                    transform: 'translateY(-2px)'
                  }
                }}
              />
            ))}
          </Tabs>
        </Box>

        {/* Services Grid */}
        <Box display='grid' gridTemplateColumns={{ xs: '1fr', lg: '1fr 1fr' }} gap={4} data-aos='fade-up'>
          {currentServices.map((service, index) => (
            <StyledServiceCard
              key={`${activeTab}-${index}`}
              sx={{
                background: `linear-gradient(135deg, ${primaryColorConfig[0].main} 0%, ${primaryColorConfig[1].main} 100%)`,
                minHeight: { xs: 'auto', md: '600px' }
              }}
            >
              {/* Glow Effect */}
              <Box
                className='service-glow'
                sx={{
                  position: 'absolute',
                  top: -50,
                  left: -50,
                  right: -50,
                  bottom: -50,
                  background: `radial-gradient(circle, ${service.glowColor} 0%, transparent 70%)`,
                  opacity: 0,
                  transition: 'all 0.5s ease',
                  zIndex: 0
                }}
              />

              <Box sx={{ p: { xs: 4, md: 5 }, position: 'relative', zIndex: 1, height: '100%' }}>
                {/* Popular Badge */}
                {service.popular && (
                  <Chip
                    label='🔥 TERPOPULER'
                    sx={{
                      position: 'absolute',
                      top: 20,
                      right: 20,
                      backgroundColor: 'rgba(255,255,255,0.2)',
                      color: 'white',
                      fontWeight: 700,
                      backdropFilter: 'blur(10px)',
                      fontSize: '0.9rem',
                      animation: 'pulse 2s infinite'
                    }}
                  />
                )}

                {/* Duration Badge */}
                <Chip
                  label={`⏱️ ${service.duration}`}
                  sx={{
                    position: 'absolute',
                    top: 20,
                    left: 20,
                    backgroundColor: 'rgba(255,255,255,0.15)',
                    color: 'white',
                    fontWeight: 600,
                    backdropFilter: 'blur(10px)',
                    fontSize: '0.85rem'
                  }}
                />

                {/* Service Icon */}
                <ServiceIcon
                  className='service-icon'
                  sx={{
                    background: 'rgba(255,255,255,0.15)',
                    backdropFilter: 'blur(20px)',
                    border: '2px solid rgba(255,255,255,0.2)',
                    boxShadow: `0 15px 35px ${primaryColorConfig[0].light}`,
                    mt: 4
                  }}
                >
                  {service.icon}
                </ServiceIcon>

                {/* Content */}
                <Box textAlign='center' mb={4}>
                  <Typography
                    variant='h3'
                    sx={{
                      fontWeight: 800,
                      color: 'white',
                      fontSize: { xs: '1.6rem', md: '2rem' },
                      mb: 1,
                      textShadow: '0 4px 20px rgba(0,0,0,0.3)'
                    }}
                  >
                    {service.title}
                  </Typography>

                  <Typography
                    variant='h6'
                    sx={{
                      color: 'rgba(255,255,255,0.8)',
                      fontSize: '1.1rem',
                      fontWeight: 500,
                      mb: 3
                    }}
                  >
                    {service.subtitle}
                  </Typography>

                  <Typography
                    variant='body1'
                    sx={{
                      color: 'rgba(255,255,255,0.9)',
                      lineHeight: 1.6,
                      fontSize: { xs: '0.95rem', md: '1rem' },
                      mb: 3
                    }}
                  >
                    {service.description}
                  </Typography>
                </Box>

                {/* Features */}
                <Box>
                  <Typography
                    variant='h6'
                    sx={{
                      color: 'white',
                      fontWeight: 700,
                      mb: 3,
                      fontSize: '1.3rem',
                      textAlign: 'center'
                    }}
                  >
                    ✨ Fasilitas Termasuk
                  </Typography>

                  <FeatureGrid>
                    {service.features.map((feature, idx) => (
                      <Box
                        key={idx}
                        className='feature-item'
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 2,
                          p: 2,
                          background: feature.highlight ? 'rgba(255,255,255,0.15)' : 'rgba(255,255,255,0.1)',
                          borderRadius: '12px',
                          backdropFilter: 'blur(10px)',
                          border: feature.highlight
                            ? '2px solid rgba(255,255,255,0.3)'
                            : '1px solid rgba(255,255,255,0.1)',
                          transition: 'all 0.3s ease',
                          color: 'rgba(255,255,255,0.9)',
                          '&:hover': {
                            background: 'rgba(255,255,255,0.15)',
                            transform: 'translateY(-2px)'
                          }
                        }}
                      >
                        <Box sx={{ fontSize: '1.3rem', minWidth: '1.8rem' }}>{feature.icon}</Box>
                        <Typography
                          variant='body2'
                          sx={{
                            fontSize: { xs: '0.85rem', md: '0.9rem' },
                            fontWeight: feature.highlight ? 600 : 500,
                            color: feature.highlight ? 'white' : 'rgba(255,255,255,0.9)'
                          }}
                        >
                          {feature.name}
                        </Typography>
                      </Box>
                    ))}
                  </FeatureGrid>
                </Box>
              </Box>
            </StyledServiceCard>
          ))}
        </Box>

        {/* CTA Section */}
        <Box textAlign='center' mt={10} data-aos='fade-up' data-aos-delay='400'>
          <Typography
            variant='h3'
            sx={{
              fontWeight: 700,
              color: 'white',
              mb: 3,
              fontSize: { xs: '1.8rem', md: '2.2rem' }
            }}
          >
            🤝 Konsultasi Gratis
          </Typography>
          <Typography
            variant='h6'
            sx={{
              color: 'rgba(255,255,255,0.8)',
              maxWidth: '600px',
              mx: 'auto',
              mb: 4
            }}
          >
            Tim profesional kami siap membantu Anda memilih paket yang tepat dan memberikan pelayanan terbaik
          </Typography>

          <Button
            href='tel:+6282118546605'
            variant='contained'
            sx={{
              px: 6,
              py: 3,
              fontSize: '1.1rem',
              fontWeight: 700,
              borderRadius: '30px',
              background: `linear-gradient(135deg, ${primaryColorConfig[0].main} 0%, ${primaryColorConfig[1].main} 100%)`,
              color: 'white',
              boxShadow: `0 15px 35px ${primaryColorConfig[0].light}`,
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-3px)',
                boxShadow: `0 20px 40px ${primaryColorConfig[0].dark}`
              }
            }}
          >
            📞 Hubungi Kami 24/7
          </Button>
        </Box>
      </Container>
    </Box>
  )
}

export default Service
